/**
 * Keyboard arithmetic for `BlockPicker`'s 2-column × 4-row grid.
 *
 * `src/ui/roving.ts`'s `nextIndex` is the model for splitting this out as a
 * pure function — see that file's own header — but it cannot be reused
 * here: it is one-dimensional. Its `orientation: 'both'` treats `ArrowDown`
 * as a plain `+1`, which is correct for a single row of options (the pay
 * step segmented control) and wrong for a grid, where "down" means "the
 * next row, same column" — an offset of `columns`, not `1`.
 *
 * Kept apart from the DOM so the arithmetic — the part that can quietly be
 * wrong — is checked without a browser. `BlockPicker.tsx` is the DOM half:
 * it calls this to find the target index, then moves focus there itself.
 *
 * `null` matters as much as any numeric answer: it tells the caller this
 * key means nothing to the grid, so it must not call `preventDefault` and
 * swallow a key the page or the browser still owns — Tab chief among them.
 */
export function nextBlockIndex(
  key: string,
  current: number,
  count: number,
  columns: number,
): number | null {
  if (count <= 0) return null

  // Outside [0, count) means nothing is selected yet, or the value is
  // stale. Right/Down enter the grid from its start; Left/Up enter from
  // its end — the same convention roving.ts's nextIndex uses for
  // ArrowRight/ArrowLeft, extended to the vertical axis.
  const from = current >= 0 && current < count ? current : -1

  switch (key) {
    case 'ArrowRight':
      return from < 0 ? 0 : (from + 1) % count
    case 'ArrowLeft':
      return from < 0 ? count - 1 : (from - 1 + count) % count
    case 'ArrowDown':
      return from < 0 ? 0 : (((from + columns) % count) + count) % count
    case 'ArrowUp':
      return from < 0 ? count - 1 : (((from - columns) % count) + count) % count
    case 'Home':
      return 0
    case 'End':
      return count - 1
    default:
      return null
  }
}
